import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Tabs from '@/components/Tabs'
import { AuthType } from '@/components/Tabs/index.type'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
import CountryCodeSelector from '@/components/CountryCodeSelector'
// import OtherLoginItems from '@/components/OtherLoginItems'
import { Toast, Checkbox } from 'react-vant';
import { useTranslation } from 'react-i18next'
import { useSendCode } from '@/hooks/useSendCode'
import { APIWeb } from '@/api'
import { APP_NAME } from '@/config/constants'

export default React.memo(function Register() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selectedType, setSelectedType] = useState<AuthType>(AuthType.Email)
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [countryCode, setCountryCode] = useState('+86')
  const [errors, setErrors] = useState<{ email?: string; phone?: string }>({})
  const [isAgreed, setIsAgreed] = useState(false)
  const { sendCode, sendCodeLoading } = useSendCode()

  const handleCountryCodeChange = useCallback((code: string) => {
    if (code !== '+86') {
      return Toast.info(t('暂不支持该国家地区手机号注册'))
    }
    setCountryCode(code)
  }, [t])
  // 验证邮箱格式
  const validateEmail = useCallback((emailValue: string) => {
    if (!emailValue) {
      return t('请输入邮箱地址')
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailValue)) {
      return t('请输入有效的邮箱地址')
    }
    return ''
  }, [t])

  // 验证手机号格式（6-15位数字）
  const validatePhone = useCallback((phoneValue: string) => {
    if (!phoneValue) {
      return t('请输入手机号')
    }
    const phoneRegex = /^\d{6,15}$/
    if (!phoneRegex.test(phoneValue)) {
      return t('请输入有效的手机号6至15位数字')
    }
    return ''
  }, [t])

  // 处理邮箱输入变化
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value)
    const error = validateEmail(value)
    setErrors(prev => ({ ...prev, email: error }))
  }, [validateEmail])

  // 处理手机号输入变化
  const handlePhoneChange = useCallback((value: string) => {
    setPhone(value)
    const error = validatePhone(value)
    setErrors(prev => ({ ...prev, phone: error }))
  }, [validatePhone])

  // 切换登录注册类型
  const onChange = useCallback((type: AuthType) => {
    setSelectedType(type)
  }, [])

  // 检查表单是否有效
  const isFormValid = useMemo(() => {
    if (selectedType === AuthType.Email) {
      return email && !validateEmail(email)
    }
    if (selectedType === AuthType.Phone) {
      return phone && !validatePhone(phone)
    }
    return false
  }, [selectedType, email, phone, validateEmail, validatePhone])

  // 处理注册提交
  const handleSendCode = useCallback(async () => {
    if (selectedType === AuthType.Email) {
      const emailError = validateEmail(email)
      if (emailError) {
        setErrors(prev => ({ ...prev, email: emailError }))
        return
      }
      
      // 发送验证码前检查账号是否已注册
      try {
        Toast.loading({message: t('验证中'),forbidClick: true})
        const response = await APIWeb.checkAccountStatus({ account: email })
        Toast.clear()
        if (response?.registered) {
          Toast.info(t('该邮箱已被注册'))
          return
        }
      } catch (error) {
        console.error('检测账号状态失败:', error)
        // 检测失败时允许继续发送验证码
      }
      
      const data = await sendCode({ account: email, scene: 'register' })
      if (data) {
        setEmail('')
        navigate(`/register/verification-code`, {
          state: { ...data, type: 'email' }
        })
      }
    } else if (selectedType === AuthType.Phone) {
      const phoneError = validatePhone(phone)
      if (phoneError) {
        setErrors(prev => ({ ...prev, phone: phoneError }))
        return
      }
      
      // 发送验证码前检查账号是否已注册
      try {
        Toast.loading({message: t('验证中'),forbidClick: true})
        const response = await APIWeb.checkAccountStatus({ account: phone })
        Toast.clear()
        if (response?.registered) {
          Toast.info(t('该手机号已被注册'))
          return
        }
      } catch (error) {
        console.error('检测账号状态失败:', error)
        // 检测失败时允许继续发送验证码
      }
      
      const data = await sendCode({ account: phone, scene: 'register' })
      if (data) {
        setPhone('')
        navigate(`/register/verification-code`, {
          state: { ...data, type: 'phone', countryCode }
        })
      }
    }
  }, [navigate, selectedType, email, phone, countryCode, validateEmail, validatePhone, sendCode, t])

  const EmailRegisterView = useMemo(
    () => (
      <div className="space-y-2">
        <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('您的电子邮箱')}</div>
        <CustomTextInput
          isErrored={Boolean(errors.email)}
          value={email}
          onChangeText={handleEmailChange}
          placeholder={t('请输入您的电子邮箱')}
          inputMode='email'
        />
        {errors.email && (
          <p className="mt-1 text-xs text-red-600">{errors.email}</p>
        )}
      </div>
    ),
    [email, handleEmailChange, errors.email, t]
  )

  const PhoneRegisterView = useMemo(
    () => (
      <div className="space-y-2">
        <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('您的手机号')}</div>
        <div className={`flex items-center h-12 rounded-2.5 border border-solid bg-white ${errors.phone ? 'border-red-500' : 'border-[#EAECF0]'}`}>
          <div className="h-full flex items-center pl-4 pr-2 [&>div]:border-none [&>div]:h-auto [&>div]:p-0 [&>div]:rounded-none cursor-pointer">
            <CountryCodeSelector
              value={countryCode}
              onChange={handleCountryCodeChange}
            />
          </div>
          <input
            type="tel"
            value={phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder={t('请输入您的手机号')}
            inputMode="numeric"
            className="flex-1 h-full px-3 outline-none text-[#1D2129] placeholder-[#C9CDD4] bg-transparent"
          />
        </div>
        {errors.phone && (
          <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
        )}
      </div>
    ),
    [phone, countryCode, handleCountryCodeChange, handlePhoneChange, errors.phone, t]
  )

  const RegisterViewRender = useMemo(
    () => (
      selectedType === AuthType.Email ? EmailRegisterView : PhoneRegisterView
    ),
    [selectedType, EmailRegisterView, PhoneRegisterView]
  )

  return (
    <div className="bg-white h-full">
      <div className="overflow-y-auto">
        <div>
          {/* Logo */}

          <div className="w-full px-6 text-center text-[#1A1A1A] flex flex-col items-center justify-center pt-14 pb-8 text-2xl">
            <img className='w-24 mb-5' src="/icon/logo.svg" alt="logo" />
            {t('创建{{name}}账户', { name: APP_NAME })}
          </div>

          <div className="px-6">
            <Tabs
              items={[
                { type: AuthType.Email, text: t('邮箱注册') },
                { type: AuthType.Phone, text: t('手机注册') }
              ]}
              defaultSelected={AuthType.Email}
              onChange={onChange}
            />
          </div>

          <div className="px-6 mt-8">
            {RegisterViewRender}
          </div>

          <div className="px-6 mt-5 flex items-center">
            <div className='flex-shrink-0 mr-2'>
              <Checkbox
                shape='square'
                checkedColor={"var(--primary-color)"}
                checked={isAgreed}
                onChange={() => setIsAgreed(prev => !prev)}
              />
            </div>
            <span className="text-sm text-gray-600">{t('我同意')}</span>
            <Link to="/privacy-policy" className="text-sm text-[var(--primary-color)]">
              {t('隐私政策')}
            </Link>
          </div>

          <div className="px-6 mt-8">
            <CustomButton
              onClick={handleSendCode}
              loading={sendCodeLoading}
              disabled={!isAgreed || sendCodeLoading || !isFormValid}
            >
              {t('注册')}
            </CustomButton>
          </div>

          <div className="px-6 mt-6 flex items-center justify-center space-x-2">
            <span className="text-sm text-gray-600">{t('已有账户')}</span>
            <Link to="/login" className="text-sm text-[var(--primary-color)]">
              {t('登录')}
            </Link>
          </div>
        </div>
        {/* Other register options */}
        {/* <div className="px-6 mt-12 pb-8">
          <OtherLoginItems />
        </div> */}
      </div>
    </div>
  )
})